import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Fab,
  Grid,
  TextField,
  Typography,
  CircularProgress,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { ERROR_MESSAGE, OK_MESSAGE } from "../../utils/Constants";
import { CheckCircleOutline } from "@mui/icons-material";
import { messageType } from "../../types/otherTypes";
import { useNavigate } from "react-router-dom";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import { CategoriesType, CourseFormType } from "../../types/entitiesModel";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs/AdapterDayjs";
import dayjs from "dayjs";
import { AuthContext } from "../../context/AuthContext";


const EditCourse: React.FC = () => {
  const { token } = React.useContext(AuthContext) || {};
  const [categories, setCategories] = React.useState<CategoriesType[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const navigate = useNavigate();

  const [formState, setFormState] = React.useState<CourseFormType>({
    category_id: 1,
    group_name: "",
    start_time: dayjs().toString(),
    end_time: dayjs().toString(),
    day_of_week: "",
    monthly_fee: 0.0,
    enrollment_fee: 0.0,
  });

  const [loading, setLoading] = React.useState(true);
  const [message, setMessage] = React.useState<messageType>({
    message: "",
    type: OK_MESSAGE,
  });

  const daysOfWeek = [
    "Lunes",
    "Martes",
    "Miercoles",
    "Jueves",
    "Viernes",
    "Sabado",
    "Domingo",
  ];

  /**
   * To go to the users table
   */
  const handleBackView = () => {
    navigate("/admin/courses");
  };

  /**
   * To handle form data and category selection
   *
   * @param event
   */
  const handleChangeForm = (
    event:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent
  ) => {
    const { name, value } = event.target;

    if (name === "category_name") {
      setFormState((prevState) => ({
        ...prevState,
        category_name: name,
      }));
    } else {
      setFormState((prevState: any) => ({
        ...prevState,
        [name]: value,
      }));
    }
  };

  /**
   * to have categories to select
   */
  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await fetch(`appservice/categories/all`);

      if (!response.ok) {
        throw new Error("Error al obtener las categorías");
      }

      const jsondata = await response.json();

      setCategories(jsondata);
      setError(null);
    } catch (err) {
      setError("Error fetching categories");
      console.error("Error al cargar las categorías:", err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Send data to api, to save the user
   */
  const handleSubmit = async () => {
    const requestBody = {
      category_id: formState.category_id,
      group_name: formState.group_name,
      start_time: dayjs(formState.start_time).format("HH:mm:ss"),
      end_time: dayjs(formState.end_time).format("HH:mm:ss"),
      day_of_week: formState.day_of_week,
      monthly_fee: formState.monthly_fee,
      enrollment_fee: formState.enrollment_fee,
    };

    try {
      const response = await fetch(`/api/courses`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        const responseData = await response.json();

        setMessage({
          message: `Success: ${
            responseData.messages || "Data enviada satisfactoriamente"
          }`,
          type: OK_MESSAGE,
        });

        setTimeout(() => {
          navigate("/admin/courses");
        }, 1000);
      } else {
        const errorData = await response.json();
        setMessage({
          message: `Error: ${errorData.message || "Algo ha ido mal"}`,
          type: ERROR_MESSAGE,
        });
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        setMessage({
          message: `Error: ${
            error.message || "Un error desconocido ha ocurrido"
          }`,
          type: ERROR_MESSAGE,
        });
      } else {
        setMessage({
          message: "Error: Error desconocido",
          type: ERROR_MESSAGE,
        });
      }
    }
  };

  React.useEffect(() => {
    if (token) {
      fetchCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <React.Fragment>
      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100vh"
        >
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={0}>
          {error && (
            <Alert sx={{ mx: "auto", my: 2, width: "95%" }} severity="error">
              {error}
            </Alert>
          )}
          {message.message !== "" &&
            (message.type === OK_MESSAGE ? (
              <Alert
                icon={<CheckCircleOutline fontSize="inherit" />}
                severity="success"
                sx={{ mx: "auto", mb: 2, width: "95%" }}
              >
                {message.message}
              </Alert>
            ) : (
              <Alert sx={{ m: "auto", mb: 2, width: "95%" }} severity="error">
                {message.message}
              </Alert>
            ))}
          <Fab
            color="primary"
            variant="extended"
            sx={{ mr: 1, mb: { xs: 1, sm: 0, lg: 0 }, ml: 2 }}
            onClick={handleBackView}
          >
            <KeyboardDoubleArrowLeftIcon />
            <Typography sx={{ ml: 1, textTransform: "capitalize" }}>
              {"Tabla de Cursos"}
            </Typography>
          </Fab>
          <Grid item lg={12} md={12} xs={12}>
            <div>
              <Card variant="outlined" sx={{ p: 0 }}>
                <Box
                  sx={{ padding: "15px 30px" }}
                  display="flex"
                  alignItems="center"
                >
                  <Box flexGrow={1}>
                    <Typography sx={{ fontSize: "18px", fontWeight: "500" }}>
                      Creación del curso
                    </Typography>
                  </Box>
                </Box>
                <Divider />
                <CardContent sx={{ padding: "30px" }}>
                  <form>
                    {/* Nombre y categoría */}
                    <Grid container spacing={2}>
                      <Grid item md={6} xs={12}>
                        <TextField
                          id="group_name"
                          label="Nombre"
                          variant="outlined"
                          placeholder="Nombre"
                          fullWidth
                          sx={{ mb: 2 }}
                          name="group_name"
                          onChange={handleChangeForm}
                        />
                        <TextField
                          fullWidth
                          id="category_id"
                          variant="outlined"
                          select
                          label="Categoría"
                          name="category_id"
                          value={formState.category_id}
                          onChange={handleChangeForm}
                          sx={{ mb: 2 }}
                        >
                          {Array.isArray(categories) &&
                          categories.length > 0 ? (
                            categories.map((category) => (
                              <MenuItem key={category.id} value={category.id}>
                                {category.name}
                              </MenuItem>
                            ))
                          ) : (
                            <MenuItem disabled value="">
                              No categories available
                            </MenuItem>
                          )}
                        </TextField>
                        <TextField
                          id="enrollment_fee"
                          label="Cuota de Inscripción"
                          variant="outlined"
                          placeholder="Cuota de Inscripción"
                          fullWidth
                          sx={{ mb: 2 }}
                          name="enrollment_fee"
                          onChange={(e) => handleChangeForm(e)}
                        />
                        <TextField
                          id="monthly_fee"
                          label="Mensualidad"
                          variant="outlined"
                          placeholder="Mensualidad"
                          fullWidth
                          sx={{ mb: 2 }}
                          name="monthly_fee"
                          onChange={(e) => handleChangeForm(e)}
                        />
                      </Grid>
                      {/* Hora de inicio hora de fin */}
                      <Grid item md={6} xs={12}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <TimePicker
                            label="Hora de inicio"
                            sx={{ width: "100%", mb: 2 }}
                            value={dayjs(formState.start_time)}
                            onChange={(newValue) => {
                              setFormState((prevState: any) => ({
                                ...prevState,
                                start_time: newValue?.toString(),
                              }));
                            }}
                          />
                          <TimePicker
                            label="Hora de fin"
                            sx={{ width: "100%", mb: 2 }}
                            value={dayjs(formState.end_time)}
                            onChange={(newValue) => {
                              setFormState((prevState: any) => ({
                                ...prevState,
                                end_time: newValue?.toString(),
                              }));
                            }}
                          />
                        </LocalizationProvider>
                        <TextField
                          fullWidth
                          id="day_of_week"
                          variant="outlined"
                          select
                          label="Día de la semana"
                          name="day_of_week"
                          value={formState.day_of_week}
                          onChange={handleChangeForm}
                          sx={{ mb: 2 }}
                        >
                          {daysOfWeek.map((day) => (
                            <MenuItem key={day} value={day}>
                              {day}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    </Grid>
                    <Button
                      onClick={handleSubmit}
                      variant="contained"
                      color="primary"
                      sx={{ mt: 2 }}
                    >
                      Guardar
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </Grid>
        </Grid>
      )}
    </React.Fragment>
  );
};

export default EditCourse;
